import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import { IGetListRequest, IWCBAllProvincesCode } from "../../models";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { WCBAllProvincesCodeSetupInfoFieldsPanel } from "./Panels/WCBAllProvincesCodeSetupInfoFieldsPanel";
import { WCBAllProvincesCodeSetupToolbar } from "./WCBAllProvincesCodeSetupToolbar";
import { WCBAllProvincesCodeSetupValidationSchema } from "./validator";
import { WCBService } from "../../services/WCBService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IWCBAllProvincesCodeSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "code-setup-single-data-record";
const WCB_ALL_PROVINCES_CODE_LIST_DATA_QUERY_KEY = "code-setup-list";

const useGetData = (
	wcbProvinceSlug: string,
	provinceId: string | null,
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, wcbProvinceSlug, provinceId, navigateToLogout],
		queryFn: () => {
			return WCBService.getAllProvincesCodeById(wcbProvinceSlug, provinceId, clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetAllProvincesCodesList = (
	wcbProvinceSlug: string,
	provinceId: string | null,
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [WCB_ALL_PROVINCES_CODE_LIST_DATA_QUERY_KEY, provinceId, wcbProvinceSlug],
		queryFn: (data) => {

			return WCBService.getAllProvincesCodes(
				wcbProvinceSlug,
				provinceId,
				clientId,
				taxYear,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});
const useGetAllProvincesCodeDDL = (
	wcbProvinceSlug: string, provinceId: any, taxYear: number) =>
	useQuery({
		queryKey: ["wcb-all-provinces-code-ddl", wcbProvinceSlug, provinceId],
		queryFn: (data) => {
			return WCBService.getAllProvincesCodesDDL(wcbProvinceSlug, provinceId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
export const WCBAllProvincesCodeSetup: React.FC<IWCBAllProvincesCodeSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [codeSetupId, setCodeSetupId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const [provinceId, setProvinceId] = useState<any>(null);
	const { id, wcbProvinceName } = useParams();

	const [wcbProvinceSlug, setWCBProvinceSlug] = useState<string>("");
	useEffect(() => {
		if (wcbProvinceName && wcbProvinceName.length > 0) {

			setWCBProvinceSlug(wcbProvinceName);
			if (wcbProvinceName !== wcbProvinceSlug) {
				formMethods.setValue('code', undefined)
				setEditRecordId(undefined)
			}
		}
	}, [wcbProvinceSlug, wcbProvinceName]);
	const countryAndProvincesInfo = useSelector((state: IAppState) => state.countryAndProvincesInfo)
	const canadaProvince = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces)

	useEffect(() => {
		const updatedCanadaProvincesLabels = canadaProvince && canadaProvince.map((obj: any) => ({ ...obj, label: obj.label.toLowerCase().replace(/\s+/g, '_') }));

		const currentLoadedProvince = updatedCanadaProvincesLabels?.find((x: any) => x.label === wcbProvinceSlug)

		if (currentLoadedProvince) {
			setProvinceId(currentLoadedProvince.value)
		}

	}, [wcbProvinceSlug, canadaProvince]);

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useGetAllProvincesCodesList(
		wcbProvinceSlug,
		provinceId,
		clientId,
		taxYear,
		navigateToLogout
	);

	const wcbAllProvincesCodeSetupList: any | null = data;

	const {
		isPending: isPendingDefaultCodeDDL,
		data: defaultCodeDDL,
	} = useGetAllProvincesCodeDDL(wcbProvinceSlug, provinceId, taxYear);

	const { isPending: isSubmitting, mutate: submitAllProvincesCodeSetupFormFields } =
		useMutation({
			mutationFn: WCBService.createAllProvincesCodes,
			onSuccess: (res: IWCBAllProvincesCode | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIWCBAllProvincesCodeResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/taxes/wcb/code/${wcbProvinceSlug}/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WCB_ALL_PROVINCES_CODE_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIWCBAllProvincesCodeResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/taxes/wcb/code/${wcbProvinceSlug}/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WCB_ALL_PROVINCES_CODE_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		wcbProvinceSlug,
		provinceId,
		clientId,
		editRecordId,
		navigateToLogout
	);

	const updatedWCBAllProvincesCodeSetupListItems = useMemo(() => {
		return (
			wcbAllProvincesCodeSetupList &&
			wcbAllProvincesCodeSetupList.items &&
			wcbAllProvincesCodeSetupList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/taxes/wcb/code/${wcbProvinceSlug}/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.code}
					</Link>
				),
				rate: (item.rate),
				description: (item.description)
			}))
		);
	}, [wcbAllProvincesCodeSetupList]);


	const DEFAULT_VALUES = {
		code: undefined

	};

	const formMethods = useForm<any>({
		resolver: yupResolver(WCBAllProvincesCodeSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		const currentProvince = canadaProvince?.find(
			(province: any) => province.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug
		);
		let payload = {
			clientId: clientId,
			id: id && id,
			wcbProvinceSlug: wcbProvinceSlug,
			navigateToLogout: navigateToLogout,
			code: values.code
		};
		submitAllProvincesCodeSetupFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteProvinceCodesById } = useMutation({
		mutationFn: WCBService.delAllProvincesCodes,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Codes fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/taxes/wcb/code/${wcbProvinceSlug}`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WCB_ALL_PROVINCES_CODE_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteCodesField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wcbProvinceSlug: wcbProvinceSlug,
				codeId: codeSetupId,
				navigateToLogout: navigateToLogout,
			};
			deleteProvinceCodesById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setCodeSetupId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setCodeSetupId(params.id);
	}, []);



	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/taxes/wcb/code/${wcbProvinceSlug}`;
		navigate(path);
	};

	const expandedLabels = ["WCB/WSIB", `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}`];
	const expandedWCBAllProvincesCodeSetupOrSetupItem = "Code Setup";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}` &&
				item.label === expandedWCBAllProvincesCodeSetupOrSetupItem
			) {
				isExpanded = true;
			}
			let itemPath = item.path;

			if (
				parentLabel === `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}`
				&& wcbProvinceSlug
				&& item.label === expandedWCBAllProvincesCodeSetupOrSetupItem
			) {
				itemPath = `/taxes/wcb/code/${wcbProvinceSlug}`;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: itemPath,
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		codesName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions, wcbProvinceSlug]);

	const onRowEditClicked = useCallback((params: IWCBAllProvincesCode | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/taxes/wcb/code/${wcbProvinceSlug}/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{`${wcbProvinceSlug === 'federal' ? 'Federal' : canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label} Code Setup`}
			</div>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isPendingDefaultCodeDDL}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteCodesField}
				cancelActionHandler={onCloseHandler}
				testId="code-setup-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Code Setup"
										testId={`code-setup`}
										showTitle={false}
										variant="outlined"
									>
										<WCBAllProvincesCodeSetupInfoFieldsPanel codesDDL={defaultCodeDDL && sortDDLByLabel(defaultCodeDDL)} />
									</StandardPanel>
								}
								toolbar={
									<WCBAllProvincesCodeSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									wcbAllProvincesCodeSetupList && updatedWCBAllProvincesCodeSetupListItems
										? updatedWCBAllProvincesCodeSetupListItems
										: []
								}
								columns={[{ field: "label", headerName: "Code" }, { field: "rate", headerName: "Rate" }, { field: "description", headerName: "Description" }]}
								showHeaderColumns={true}

								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								showArchiveIcon={false}
								isLoading={isPending}

								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								showPaginator={false}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"codes-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
